<template>
  <div v-if="service">
    <div class="entete">
      <div class="level">
        <div class="level-left">
          <div class="columns is-gapless">
            <div class="column">
              <h1 class="title is-size-3"> {{ getTitle }} </h1>
              <app-breadcrumb :breadcrumb="breadcrumb"/>
            </div>
          </div>
        </div>
        <div class="level-right">
          <div class="buttons">
            <button class="button" @click="$router.go(-1)">
              {{ $t('general.button.cancel')}}
            </button>
            <button class="button is-addOrEdit" @click="save()">
              {{ $t('general.button.sauvegarder') }}
            </button>
            <piece-jointe-button
              class="mgr-2"
              :perm="permission.maj">
            </piece-jointe-button>
          </div>
        </div>
      </div>
    </div>
    <error-msg-component
      v-if="error"
      :error="error"
      :messages="messages"
      :champs-vide="champsVide"
      :messages-champs-vide="messagesChampsVide"
      @close="error = $event"
      class="mgt-20px"
      title="general.problemOccurred"
      withEmplyFields/>
    <div class="tab">
      <ul>
        <li
          v-for="(tab, index) in tabs" :key="index"
          :class="{'is-active': activeTab === tab}"
          @click="activeTab = tab">
          {{ $t(`addEditFournisseurs.tabs.${tab}`) }}
        </li>
      </ul>
    </div>
    <section class="corps">
      <component
        :is="activeTab"
        :detail="false"
        :fournisseur="service"/>
    </section>
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import { createNamespacedHelpers } from 'vuex';
import { get } from '@/api/tiers';
import { getAllByTiers } from '@/api/contacts';
import { insertTiers, updateTiers } from '@/services/tiers/saveTiers';
import genererClient from '@/services/tiers/genererTiers';
import initialize from '@/services/tiers/initializeTiers';
import messageErreur from '@/services/general/messageErreur';
import messageSucces from '@/services/general/messageSucces';
import validationGeneral, { removeDuplicateMsgError } from '@/services/validation/validation';
import champsFournisseurTranslator from '@/services/tiers/champsFournisseurTranslator';
import { SERVICE } from '@/configuration/general/permissionsParametrageConf';
import {
  TIERS_TYPE_FOURNISSEUR,
  TIERS_SOUS_TYPE_SERVICE,
} from '@/configuration/tiers/tiersConf';
import AppBreadcrumb from '@/components/general/breadcrumb';
import ErrorMsgComponent from '@/components/general/errorMsgComponent';
import CompetencesTab from '@/components/achat/fournisseurs/addEdit/competencesTab';
import InformationsTab from '@/components/achat/fournisseurs/addEdit/informationsTab';
import PieceJointeButton from '@/components/general/button/pieceJointeButton';

const Societe = createNamespacedHelpers('societe');
const Attachments = createNamespacedHelpers('attachments');

export default {
  name: 'MService',
  components: {
    CompetencesTab,
    InformationsTab,
    AppBreadcrumb,
    ErrorMsgComponent,
    PieceJointeButton,
  },
  data() {
    return {
      permission: SERVICE,
      service: null,
      libelle: '',
      error: false,
      messages: [],
      nomCommercial: '',
      champsVide: [],
      messagesChampsVide: [],
      activeTab: 'informationsTab',
    };
  },
  computed: {
    ...Societe.mapGetters({
      societeActiveId: 'societeActiveId',
    }),
    tabs() {
      return ['informationsTab', 'competencesTab'];
    },
    dataAlreadyExist() {
      return this.service && !!(this.service.id);
    },
    getTitle() {
      return this.$route.params.id
        ? this.$t('editionService.titleModification', { libelle: this.libelle })
        : this.$t('editionService.titleCreation');
    },
    breadcrumb() {
      return [
        { title: this.$i18n.t('general.breadcrumb.racine'), route: this.$PAGE_DEFAULT, isActive: false },
        { title: this.$t('parametrage.title'), route: { name: 'l.parametrage.general' }, isActive: false },
        { title: this.$i18n.t('listeService.breadcrumb.liste'), route: { name: 'l.parametrage.service' }, isActive: false },
        {
          title: this.$route.params.id ? this.$t('editionService.titleModification', { libelle: this.libelle }) : this.$t('editionService.titleCreation'),
          route: { name: '' },
          isActive: true,
        },
      ];
    },
  },
  methods: {
    ...Attachments.mapActions({
      insertAttachments: 'insert',
      updateAttachments: 'update',
      setAttachmentContext: 'setContext',
      setAttachmentElementId: 'setElementId',
    }),
    isType(type) {
      return this.service.sousTypes.some((el) => el === type);
    },
    async save() {
      this.error = false;
      this.messages = [];
      this.champsVide = [];
      this.messagesChampsVide = [];
      const loadingComponent = this.$buefy.loading.open();
      try {
        let service = cloneDeep(this.service);
        if (!this.dataAlreadyExist) {
          service.contacts.forEach((el) => {
            delete el.guid;
          });
          service.societeId = this.societeActiveId;
          service = await insertTiers(service);
          await this.insertAttachments(service.id);
          messageSucces(this.$i18n.t('addEditFournisseurs.notifications.successCreation'));
        } else {
          service = await updateTiers(service);
          await this.updateAttachments(service.id);
          messageSucces(this.$i18n.t('addEditFournisseurs.notifications.successChange'));
        }
        this.$router.push({
          name: 'c.parametrage.service',
          params: { id: service.id },
        });
      } catch (e) {
        if (e.response?.data?.error) {
          validationGeneral(this.$i18n, e.response, this.champsVide,
            this.messages, this.messagesChampsVide, champsFournisseurTranslator);
          this.messages = removeDuplicateMsgError(this.messages);
          this.error = true;
        } else messageErreur(this.$i18n);
        throw e;
      } finally {
        loadingComponent.close();
      }
    },
  },
  async mounted() {
    const loadingComponent = this.$buefy.loading.open();
    try {
      await this.setAttachmentContext({ module: 'Client' });
      let serviceGenerer = genererClient();
      serviceGenerer.type = TIERS_TYPE_FOURNISSEUR;
      serviceGenerer.sousTypes = [TIERS_SOUS_TYPE_SERVICE];
      serviceGenerer.interne = true;
      if (this.$route.params.id) {
        serviceGenerer = (await get(this.$route.params.id)).data.result;
        serviceGenerer.contacts = (await getAllByTiers(serviceGenerer.id)).data.result.items;
        this.libelle = cloneDeep(serviceGenerer.nomCommercial);
        this.setInformationPage(this.$t('pageGeneral.modification', { item: this.libelle }));
        await this.setAttachmentElementId({ id: serviceGenerer.id });
        initialize(serviceGenerer);
      } else {
        this.setInformationPage(this.$t('pageParametrage.service.creation'));
      }
      this.service = cloneDeep(serviceGenerer);
    } catch (e) {
      messageErreur(this.$i18n);
      throw e;
    } finally {
      loadingComponent.close();
    }
  },
};
</script>
